import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Heart, Users } from 'lucide-react';

const RegisterPage = () => {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialRole = searchParams.get('role') || 'guest';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: initialRole,
    accepted_terms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.accepted_terms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setLoading(true);

    try {
      const user = await register(formData);
      toast.success(t('common.success'));
      
      if (user.role === 'host') {
        navigate('/host/dashboard');
      } else {
        navigate('/guest/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12 bg-secondary">
        <Card className="w-full max-w-md" data-testid="register-card">
          <CardHeader className="text-center">
            <CardTitle className="font-serif text-3xl">{t('auth.register.title')}</CardTitle>
            <CardDescription>
              {t('auth.register.hasAccount')}{' '}
              <Link to="/login" className="text-primary hover:underline">
                {t('auth.register.login')}
              </Link>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.register.name')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  data-testid="register-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.register.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.register.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>{t('auth.register.role')}</Label>
                <RadioGroup
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem value="host" id="host" className="peer sr-only" />
                    <Label
                      htmlFor="host"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-white p-4 hover:bg-secondary hover:text-primary peer-data-[state=checked]:border-primary cursor-pointer transition-all"
                      data-testid="role-host"
                    >
                      <Heart className="h-6 w-6 mb-2" />
                      <span className="text-sm font-medium">{t('auth.register.roleHost')}</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="guest" id="guest" className="peer sr-only" />
                    <Label
                      htmlFor="guest"
                      className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-white p-4 hover:bg-secondary hover:text-primary peer-data-[state=checked]:border-primary cursor-pointer transition-all"
                      data-testid="role-guest"
                    >
                      <Users className="h-6 w-6 mb-2" />
                      <span className="text-sm font-medium">{t('auth.register.roleGuest')}</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="accepted_terms"
                  name="accepted_terms"
                  checked={formData.accepted_terms}
                  onCheckedChange={(checked) => setFormData({ ...formData, accepted_terms: checked })}
                  data-testid="register-terms"
                />
                <Label htmlFor="accepted_terms" className="text-sm text-muted-foreground leading-tight">
                  {t('auth.register.terms')}{' '}
                  <Link to="/terms" className="text-primary hover:underline">Terms</Link>
                  {' & '}
                  <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.register.button')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RegisterPage;
